import React, { createContext, useState, useContext, useEffect, useMemo } from 'react';
import { ThemeProvider as MUIThemeProvider } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { lightTheme, darkTheme } from './theme'; // Import from the same directory

const ThemeContext = createContext();

export const ThemeProvider = ({ children }) => {
  // Detect system preference
  const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)');

  // State to hold the current theme mode ('light', 'dark', or 'system')
  const [mode, setMode] = useState(() => {
    const savedMode = localStorage.getItem('themeMode');
    return savedMode || 'light'; // Default to system preference
  });

  // Persist mode changes to localStorage
  useEffect(() => {
    localStorage.setItem('themeMode', mode);
  }, [mode]);

  // Determine the active theme based on mode and system preference
  const activeTheme = useMemo(() => {
    if (mode === 'system') {
      return prefersDarkMode ? darkTheme : lightTheme;
    }
    return mode === 'dark' ? darkTheme : lightTheme;
  }, [mode, prefersDarkMode]);

  // Function to explicitly set the theme mode
  const toggleTheme = (newMode) => {
    setMode(newMode || (activeTheme.palette.mode === 'dark' ? 'light' : 'dark')); // Allow direct setting or toggle
  };

  // Provide the current mode, the toggle function, and the active theme
  const value = useMemo(() => ({
    mode,
    toggleTheme,
    setMode, // Provide setMode for more control (e.g., dropdown with light/dark/system)
    isDarkMode: activeTheme.palette.mode === 'dark',
    theme: activeTheme,
  }), [mode, activeTheme]);

  return (
    <ThemeContext.Provider value={value}>
      <MUIThemeProvider theme={activeTheme}>
        {children}
      </MUIThemeProvider>
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}; 